import React from 'react';
import PropTypes from 'prop-types';
import { animated, useSpringRef, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { MotifIconButton } from '../button/icon-button/icon-button';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/hardware/ic_keyboard_arrow_left_24px.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/hardware/ic_keyboard_arrow_right_24px.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/av/ic_pause_circle_outline_24px.svg';
import { ReactComponent as PlayIcon } from '../../assets/icons/av/ic_play_circle_outline_24px.svg';

const nextTransition = {
  from: { opacity: 0, transform: 'translate3d(100%,0,0)' },
  enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
  leave: { opacity: 0, transform: 'translate3d(-50%,0,0)' }
};

const prevTransition = {
  from: { opacity: 0, transform: 'translate3d(-100%,0,0)' },
  enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
  leave: { opacity: 0, transform: 'translate3d(50%,0,0)' }
};

const defaultIcons = {
  next: <ArrowRightIcon />,
  pause: <PauseIcon />,
  play: <PlayIcon />,
  previous: <ArrowLeftIcon />
};

/* eslint-disable complexity */
/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} title
 * @prop {boolean} [auto]
 * @prop {number} [delay]
 * @prop {{next: React.ReactNode, pause: React.ReactNode, play: React.ReactNode, previous: React.ReactNode}} [icons]
 * @prop {boolean} [infinite]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifCarousel = (props) => {
  const { auto = false, children, delay = 5000, icons: userIcons = {}, infinite = false, title } = props;
  const headingId = React.useRef(uniqueId('motif-carousel-heading-'));
  const playerInterval = React.useRef();
  const [playing, setPlaying] = React.useState(auto);
  const [isNext, setIsNext] = React.useState(true);
  const [activeSlide, setActiveSlide] = React.useState(0);
  const transRef = useSpringRef();
  const transitions = useTransition(activeSlide, {
    ref: transRef,
    keys: null,
    ...(isNext ? nextTransition : prevTransition)
  });
  const numOfSlides = React.Children.count(children);
  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  const incrementSlide = React.useCallback(() => {
    setIsNext(true);
    setActiveSlide(prevState => {
      const nextSlide = prevState + 1;

      return nextSlide >= numOfSlides ? 0 : nextSlide;
    });
  }, [numOfSlides]);

  const decrementSlide = React.useCallback(() => {
    setIsNext(false);
    setActiveSlide(prevState => {
      const prevSlide = prevState - 1;

      return prevSlide < 0 ? numOfSlides - 1 : prevSlide;
    });
  }, [numOfSlides]);

  const handlePause = React.useCallback(() => {
    setPlaying(false);
    if (playerInterval.current) {
      clearInterval(playerInterval.current);
    }
  }, []);

  const handlePlay = React.useCallback(() => {
    handlePause();
    setPlaying(true);
    playerInterval.current = setInterval(incrementSlide, delay);
  }, [handlePause, incrementSlide, delay]);

  const handleNext = () => {
    if (playing) {
      handlePause();
      incrementSlide();
      handlePlay();
    } else {
      incrementSlide();
    }
  };

  const handlePrevious = () => {
    if (playing) {
      handlePause();
      decrementSlide();
      handlePlay();
    } else {
      decrementSlide();
    }
  };

  React.useEffect(() => {
    transRef.start();
  }, [activeSlide]);

  React.useEffect(() => {
    if (auto) {
      handlePlay();
    }

    return handlePause;
  }, [auto, handlePlay, handlePause]);

  const hasSlides = numOfSlides > 1;

  return (
    <section className="motif-carousel-container" aria-labelledby={headingId.current} data-motif-carousel>
      <h3 id={headingId.current} className="motif-carousel-visually-hidden">{title}</h3>
      <ul className="motif-carousel-slides-container">
        {transitions((style, i) => (
          <animated.li style={style} className="motif-carousel-item">
            {Array.isArray(children) ? children[i] : React.Children.only(children)}
          </animated.li>
        ))}
      </ul>
      {hasSlides && (
        <ul className="motif-carousel-controls">
          {(auto || infinite || activeSlide > 0) && (
            <li>
              <MotifIconButton
                aria-label="previous"
                className="motif-carousel-previous-btn"
                type="button"
                onClick={handlePrevious}
              >
                {icons.previous}
              </MotifIconButton>
            </li>
          )}
          {(auto || infinite || activeSlide < numOfSlides - 1) && (
            <li>
              <MotifIconButton
                aria-label="next"
                className="motif-carousel-next-btn"
                type="button"
                onClick={handleNext}
              >
                {icons.next}
              </MotifIconButton>
            </li>
          )}
        </ul>
      )}
      <ul className="motif-carousel-dot-indicator">
        {React.Children.map(children, (_child, i) => {
          const isActive = i === activeSlide;

          return (
            <li className="motif-carousel-dot-item">
              <button
                aria-label={`Go to slide ${i + 1}`}
                className={classNames({
                  'motif-carousel-dot': true,
                  'motif-carousel-dot-active': isActive
                })}
                data-testid="carousel-dot"
                onClick={() => {
                  const next = i > activeSlide;
                  if (playing) {
                    handlePause();
                    setIsNext(next);
                    setActiveSlide(i);
                    handlePlay();
                  } else {
                    setIsNext(next);
                    setActiveSlide(i);
                  }
                }}
              >
                {isActive && <span className="motif-carousel-visually-hidden">(Current Item)</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {hasSlides && auto && (
        <MotifIconButton
          aria-label={playing ? 'pause' : 'play'}
          className={playing ? 'motif-carousel-pause-btn' : 'motif-carousel-play-btn'}
          type="button"
          onClick={playing ? handlePause : handlePlay}
        >
          {playing
            ? <React.Fragment><span>Pause</span>{icons.pause}</React.Fragment>
            : <React.Fragment><span>Play</span>{icons.play}</React.Fragment>
          }
        </MotifIconButton>
      )}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="motif-carousel-live-region motif-carousel-visually-hidden"
      >
        Item {activeSlide + 1} of {numOfSlides}
      </div>
    </section>
  );
};

MotifCarousel.propTypes = {
  auto: PropTypes.bool,
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  icons: PropTypes.shape({
    next: PropTypes.node,
    pause: PropTypes.node,
    play: PropTypes.node,
    previous: PropTypes.node
  }),
  infinite: PropTypes.bool,
  title: PropTypes.string.isRequired
};

export { MotifCarousel };
